import React, { useEffect, useState } from "react";
import {
    Category,
    CategoryParameter,
    ParameterDataType,
    PatchCategoryUpdateNameDTO,
    PatchCategoryUpdateDescriptionDTO,
    PatchCategoryParameterUpdateDescriptionDTO,
    PatchCategoryParameterUpdateNameDTO,
    PatchCategoryChangeParameterUnitDTO,
    SelectionType,
} from "../../types/Category";
import { Unit, UnitDimension, UnitSystem } from "../../types/Unit";
import { getAllUnits, getAllUnitDimensions, getAllUnitSystems } from "../../api/UnitAPI";
import { logApiError, getUserMessage } from "../../api/apiError";

import {
    createCategory,
    getCategoryById,
    patchCategoryUpdateName,
    patchCategoryUpdateDescription,
    patchCategoryAddParameter,
    patchCategoryRemoveParameter,
    PatchCategoryAddSelectableParameter,
    patchCategoryParameterUpdateName,
    patchCategoryParameterUpdateDescription,
    patchCategoryChangeSelectableChoice,
    patchCategoryRemoveSelectableChoice,
    PatchCategoryAddChoiceToSelectableParameter,
    patchCategoryChangeParameterUnit,
    patchCategoryAddSimilarName,
    patchCategoryChangeServiceArea,
    patchCategoryChangeItemLocation,
    patchCategoryReorderParameters,
    patchCategoryRemoveSimilarName,
} from "../../api/CategoryAPI";

import CategoryTreeNavigator from "./CategoryTreeNavigator";
import CategoryForm from "./CategoryForm";
import ParameterRequestAdminList from "../categoryRequest/ParameterRequestAdminList";
import CategoryRequestAdminList from "../categoryRequest/CategoryRequestAdminList";
import SimilarNamesEditor from "./SimilarNamesEditor";

interface CategoryComponentProps {
    token: string;
}

// helper for checking if a param is incomplete
const isIncomplete = (p: CategoryParameter) => !p.name?.trim() || !p.dataType;

type NewParamState = {
    name: string;
    description: string;
    dataType: ParameterDataType;
    unitId: string;        // INTEGER: optional single unit
    unitIds: string[];     // DOUBLE: one or more allowed units
    selectables: string[];
    selectionType: SelectionType; // ✅ must be a VALUE
};

const defaultNewParam = (): NewParamState => ({
    name: "",
    description: "",
    dataType: ParameterDataType.STRING,
    unitId: "",
    unitIds: [],
    selectables: [],
    selectionType: SelectionType.SINGLE, // ✅ default
});

const CategoryComponent: React.FC<CategoryComponentProps> = ({ token }) => {
    const [category, setCategory] = useState<Category | null>(null);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const [showNewParamForm, setShowNewParamForm] = useState(false);
    const [units, setUnits] = useState<Unit[]>([]);
    const [dimensions, setDimensions] = useState<UnitDimension[]>([]);
    const [systems, setSystems] = useState<UnitSystem[]>([]);

    // Tree-authoring state: bump to make the navigator reload after a mutation.
    const [treeRefresh, setTreeRefresh] = useState(0);

    const [newParam, setNewParam] = useState<NewParamState>(defaultNewParam());

    useEffect(() => {
        getAllUnits(token).then(setUnits).catch(() => setUnits([]));
        getAllUnitDimensions(token).then(setDimensions).catch(() => setDimensions([]));
        getAllUnitSystems(token).then(setSystems).catch(() => setSystems([]));
    }, [token]);

    const handleCategorySave = async () => {
        if (!category) return;

        setIsLoading(true);
        setError(null);
        setInfo(null);

        try {
            if (isCreating) {
                const newCategory = await createCategory(token, category);
                setCategory(newCategory);
                setIsCreating(false);
                // Surface the new leaf in the tree.
                setTreeRefresh((n) => n + 1);
            } else if (category.id) {
                setInfo(
                    "The API does not support updating category name or description. Reloading the latest category from the server."
                );
                const refreshed = await getCategoryById(token, category.id);
                setCategory(refreshed);
            }
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveCategoryNameUpdate = async (newName: string): Promise<void> => {
        setError(null);
        if (!category?.id) {
            alert("Category id is missing.");
            return;
        }

        if (category.version == null) {
            alert("Category version is missing.");
            return;
        }

        try {
            const dto: PatchCategoryUpdateNameDTO = {
                id: category.id,
                version: category.version,
                patchTask: "ChangeName",
                newName,
            };

            const updatedCategory = await patchCategoryUpdateName(token, dto);
            setCategory(updatedCategory);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        }
    };

    const handleAddSimilarName = async (similarName: string): Promise<void> => {
        setError(null);
        if (!category?.id || category.version == null) return;
        try {
            const updated = await patchCategoryAddSimilarName(token, {
                id: category.id,
                version: category.version,
                patchTask: "AddSimilarName",
                similarName,
            });
            setCategory(updated);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        }
    };

    const handleRemoveSimilarName = async (similarName: string): Promise<void> => {
        setError(null);
        if (!category?.id || category.version == null) return;
        try {
            const updated = await patchCategoryRemoveSimilarName(token, {
                id: category.id,
                version: category.version,
                patchTask: "RemoveSimilarName",
                similarName,
            });
            setCategory(updated);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        }
    };

    const handleChangeServiceArea = async (hasServiceArea: boolean): Promise<void> => {
        setError(null);
        if (!category?.id || category.version == null) return;
        try {
            await patchCategoryChangeServiceArea(token, category.id, category.version, hasServiceArea);
            const refreshed = await getCategoryById(token, category.id);
            setCategory(refreshed);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        }
    };

    const handleMoveParameter = async (index: number, delta: -1 | 1): Promise<void> => {
        setError(null);
        if (!category?.id || category.version == null) return;
        const ids = category.parameters.map((p) => p.id);
        const [moved] = ids.splice(index, 1);
        ids.splice(index + delta, 0, moved);
        try {
            const updated = await patchCategoryReorderParameters(token, category.id, category.version, ids);
            setCategory(updated);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        }
    };

    const handleChangeItemLocation = async (hasItemLocation: boolean): Promise<void> => {
        setError(null);
        if (!category?.id || category.version == null) return;
        try {
            await patchCategoryChangeItemLocation(token, category.id, category.version, hasItemLocation);
            const refreshed = await getCategoryById(token, category.id);
            setCategory(refreshed);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        }
    };

    const handleSaveCategoryDescriptionUpdate = async (
        newDescription: string
    ): Promise<void> => {
        setError(null);
        if (!category?.id) {
            alert("Category id is missing.");
            return;
        }

        if (category.version == null) {
            alert("Category version is missing.");
            return;
        }

        try {
            const dto: PatchCategoryUpdateDescriptionDTO = {
                id: category.id,
                version: category.version,
                patchTask: "ChangeDescription",
                newDescription,
            };

            const updatedCategory = await patchCategoryUpdateDescription(token, dto);
            setCategory(updatedCategory);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        }
    };

    const handleAddParameter = async () => {
        setError(null);
        if (!category) return;

        if (category.version == null) {
            console.error("Category version missing — cannot apply patch");
            return;
        }

        if (!newParam.name.trim()) return;


        let updated : Category;

        try {
            if (newParam.dataType === ParameterDataType.SELECTABLE) {
                updated = await PatchCategoryAddSelectableParameter(token, {
                    id: category.id,
                    version: category.version,
                    patchTask: "AddSelectableParameter",
                    createParameterDTO: {
                        name: newParam.name.trim(),
                        description: newParam.description,
                        dataType: ParameterDataType.SELECTABLE,
                        selectables: newParam.selectables ?? [],
                        selectionType: newParam.selectionType ?? SelectionType.SINGLE,

                    },
                });
            } else {
                const isDouble = newParam.dataType === ParameterDataType.DOUBLE;
                const isInteger = newParam.dataType === ParameterDataType.INTEGER;
                updated = await patchCategoryAddParameter(token, {
                    id: category.id,
                    version: category.version,
                    patchTask: "AddParameter",
                    createParameterDTO: {
                        name: newParam.name.trim(),
                        description: newParam.description,
                        dataType: newParam.dataType,
                        // DOUBLE: one or more units. INTEGER: optional single unit.
                        ...(isDouble ? { unitIds: newParam.unitIds ?? [] } : {}),
                        ...(isInteger && newParam.unitId ? { unitId: newParam.unitId } : {}),
                    },
                });
            }

            // ✅ Refresh category from backend
            //const updated = await getCategoryById(token, category.id);
            setCategory(updated);

            // ✅ Reset form including selectionType
            setNewParam(defaultNewParam());
            setShowNewParamForm(false);
        } catch (err) {
            logApiError(err);
            throw err;
        }
    };

    const handleSaveParameterNameUpdate = async (
        index: number,
        parameter: CategoryParameter,
        newName: string
    ) => {
        setError(null);
        console.log("handleSaveParameterNameUpdate called");

        if (!category?.id) {
            alert("Category id is missing.");
            return;
        }

        if (category.version === undefined || category.version === null) {
            alert("Category version is missing.");
            return;
        }

        if (parameter.id === undefined || parameter.id === null) {
            alert("Parameter id is missing.");
            return;
        }

        try {
            const dto: PatchCategoryParameterUpdateNameDTO = {
                id: category.id,
                version: category.version,
                parameterId: parameter.id,
                newName: newName,
                patchTask: "ChangeParameterName",
            };

            const updatedCategory = await patchCategoryParameterUpdateName(token, dto);
            setCategory(updatedCategory);
        } catch (err) {
            logApiError(err);
            throw err;
        }
    };

    const handleSaveParameterDescriptionUpdate = async (
        index: number,
        parameter: CategoryParameter,
        newDescription: string
    ) => {
        setError(null);
        console.log("handleSaveParameterNameUpdate called");

        if (!category?.id) {
            alert("Category id is missing.");
            return;
        }

        if (category.version === undefined || category.version === null) {
            alert("Category version is missing.");
            return;
        }

        if (parameter.id === undefined || parameter.id === null) {
            alert("Parameter id is missing.");
            return;
        }

        try {
            const dto: PatchCategoryParameterUpdateDescriptionDTO = {
                id: category.id,
                version: category.version,
                parameterId: parameter.id,
                newDescription: newDescription,
                patchTask: "ChangeParameterDescription",
            };

            const updatedCategory = await patchCategoryParameterUpdateDescription(token, dto);
            setCategory(updatedCategory);
        } catch (err) {
            logApiError(err);
            throw err;
        }
    };

    const handleSaveOptionEdit = async (
        paramIndex: number,
        choiceId: number,
        newName: string
    ) => {
        setError(null);
        if (!category?.id || category.version == null) return;
        const param = category.parameters[paramIndex];
        if (!param) return;

        try {
            const updated = await patchCategoryChangeSelectableChoice(token, {
                id: category.id,
                version: category.version,
                patchTask: "ChangeChoiceInSelectableParameter",
                parameterId: param.id,
                choiceId,
                newName,
            });
            setCategory(updated);
        } catch (err) {
            logApiError(err);
            throw err;
        }
    };

    const handleRemoveOption = async (paramIndex: number, choiceId: number) => {
        setError(null);
        if (!category?.id || category.version == null) return;
        const param = category.parameters[paramIndex];
        if (!param) return;

        try {
            const updated = await patchCategoryRemoveSelectableChoice(token, {
                id: category.id,
                version: category.version,
                patchTask: "RemoveChoiceFromSelectableParameter",
                parameterId: param.id,
                choiceId,
            });
            setCategory(updated);
        } catch (err) {
            logApiError(err);
            throw err;
        }
    };

    // for selectable parameters
    const handleParameterChange = async (
        index: number,
        field: keyof CategoryParameter | "options" | "selectionType",
        value: string | ParameterDataType | string[] | SelectionType
    ) => {
        if (!category) return;

        // ✅ options selection change -> PATCH
        if (field === "options") {
            const param = category.parameters[index];
            const oldOptions = param.options ?? [];
            const newOptions = value as string[];

            if (newOptions.length > oldOptions.length) {
                // An option was added
                const addedOption = newOptions.find(opt => !oldOptions.includes(opt));
                if (addedOption && category.version != null) {
                    try {
                        const updated = await PatchCategoryAddChoiceToSelectableParameter(token, {
                            id: category.id,
                            version: category.version,
                            patchTask: "AddChoiceToSelectableParameter",
                            parameterId: param.id,
                            newSelectableChoice: addedOption,
                            // where the editor placed it (append when at the end)
                            position: newOptions.indexOf(addedOption),
                        });
                        setCategory(updated);
                        return;
                    } catch (err) {
                        logApiError(err);
                        setError(getUserMessage(err));
                    }
                }
            }

            await handleSelectablePatch(index, { options: value as string[] });
            return;
        }

        // ✅ selectionType change -> PATCH (if you allow editing it)
        if (field === "selectionType") {
            await handleSelectablePatch(index, { selectionType: value as SelectionType });
            return;
        }

        // everything else -> local state only
        setCategory({
            ...category,
            parameters: category.parameters.map((p, i) =>
                i === index ? { ...p, [field]: value } : p
            ),
        });
    };

    const handleSaveParameterUnitUpdate = async (
        index: number,
        parameter: CategoryParameter,
        newUnitIds: string[]
    ) => {
        setError(null);
        if (!category?.id || category.version == null) return;
        if (parameter.id == null) return;

        try {
            const dto: PatchCategoryChangeParameterUnitDTO = {
                id: category.id,
                version: category.version,
                patchTask: "ChangeParameterUnit",
                parameterId: parameter.id,
                newUnitIds,
            };
            const updatedCategory = await patchCategoryChangeParameterUnit(token, dto);
            setCategory(updatedCategory);
        } catch (err) {
            logApiError(err);
            throw err;
        }
    };

    const handleSelectablePatch = async (
        index: number,
        patch: { options?: string[]; selectionType?: SelectionType }
    ) => {
        if (!category) return;

        if (category.version == null) {
            console.error("Category version missing — cannot patch");
            return;
        }

        const param = category.parameters[index];
        if (!param || !param.name) return;

        // Only patch selectables
        if (param.dataType !== ParameterDataType.SELECTABLE) return;

        try {
            if (patch.options) {
                const oldOptions = param.options ?? [];
                const addedOptions = patch.options.filter(opt => !oldOptions.includes(opt));

                let latestCategory: Category = category;

                for (const addedOption of addedOptions) {
                    latestCategory = await PatchCategoryAddChoiceToSelectableParameter(token, {
                        id: latestCategory.id,
                        version: latestCategory.version!,
                        patchTask: "AddChoiceToSelectableParameter",
                        parameterId: param.id,
                        newSelectableChoice: addedOption,
                        // where the editor placed it (append when at the end)
                        position: patch.options.indexOf(addedOption),
                    });
                }
                setCategory(latestCategory);
                return;
            }
        } catch (err) {
            logApiError(err);
            throw err;
        }
    };

    return (
        <div className="space-y-4">
            <CategoryTreeNavigator
                token={token}
                authoring
                refreshSignal={treeRefresh}
                selectedLeafId={!isCreating ? category?.id ?? null : null}
                onSelectLeaf={(leaf) => {
                    setError(null);
                    setInfo(null);
                    setCategory(leaf);
                    setIsCreating(false);
                }}
                onCreateLeafHere={(parentId, initialName) => {
                    setCategory({
                        id: "",
                        name: initialName ?? "",
                        description: "",
                        parameters: [],
                        parentId,
                        globallyUnique: false,
                    });
                    setIsCreating(true);
                    setError(null);
                    setInfo(null);
                }}
                onTreeChanged={() => setTreeRefresh((n) => n + 1)}
            />

            {/* Users' requests for new categories; "Create category" starts the
                normal creation flow pre-filled with the requested name. */}
            <CategoryRequestAdminList
                token={token}
                onCreateCategory={(name) => {
                    setCategory({
                        id: "",
                        name,
                        description: "",
                        parameters: [],
                        parentId: null,
                        globallyUnique: false,
                    });
                    setIsCreating(true);
                    setError(null);
                    setInfo(null);
                }}
            />

            {/* Users' requests for missing parameters; opening one selects its
                category (the tree reveals it) so the parameter can be added. */}
            <ParameterRequestAdminList
                token={token}
                onOpenCategory={async (categoryId) => {
                    setError(null);
                    setInfo(null);
                    try {
                        const full = await getCategoryById(token, categoryId);
                        setCategory(full);
                        setIsCreating(false);
                    } catch (err) {
                        logApiError(err);
                        setError(getUserMessage(err));
                    }
                }}
            />

            {isCreating && category && (
                <div className="section-box section-box--narrow">
                    <div className="form-group">
                        <label>New category name</label>
                        <input
                            value={category.name}
                            onChange={(e) =>
                                setCategory((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                            }
                            placeholder="Category name"
                        />
                    </div>
                    <div className="form-group">
                        <label>Description</label>
                        <input
                            value={category.description}
                            onChange={(e) =>
                                setCategory((prev) =>
                                    prev ? { ...prev, description: e.target.value } : prev
                                )
                            }
                            placeholder="Optional description"
                        />
                    </div>
                    <div className="form-group">
                        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 600 }}>
                            <input
                                type="checkbox"
                                checked={!!category.globallyUnique}
                                onChange={(e) =>
                                    setCategory((prev) =>
                                        prev ? { ...prev, globallyUnique: e.target.checked } : prev
                                    )
                                }
                            />
                            Reserve this name across the entire tree
                        </label>
                        <div className="item-description" style={{ marginTop: "0.25rem" }}>
                            By default a name only has to be unique among its siblings (the
                            categories under the same parent), so the same name may be reused in
                            other branches. Check this to reserve the name globally — no other
                            category or branch anywhere in the tree will be allowed to use it.
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Similar names (aliases to help users find this category)</label>
                        <SimilarNamesEditor
                            values={category.similarNames ?? []}
                            onAdd={(name) =>
                                setCategory((prev) =>
                                    prev ? { ...prev, similarNames: [...(prev.similarNames ?? []), name] } : prev
                                )
                            }
                            onRemove={(name) =>
                                setCategory((prev) =>
                                    prev ? { ...prev, similarNames: (prev.similarNames ?? []).filter((n) => n !== name) } : prev
                                )
                            }
                            disabled={isLoading}
                        />
                    </div>
                    <div className="button-group" style={{ marginTop: "0.75rem" }}>
                        <button onClick={handleCategorySave} disabled={isLoading || !category.name.trim()}>
                            {isLoading ? "Creating…" : "Create Category"}
                        </button>
                        <button
                            className="btn-secondary"
                            onClick={() => {
                                setCategory(null);
                                setIsCreating(false);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                    <div className="item-description" style={{ marginTop: "0.5rem" }}>
                        {category.parentId
                            ? "This category will be created under the selected branch."
                            : "This category will be created at the tree root."}
                    </div>
                </div>
            )}

            {info && (
                <div className="p-2 rounded" style={{ background: "var(--bg-secondary)", color: "var(--text-secondary)" }}>
                    {info}
                </div>
            )}

            {category && !isCreating && (
                <CategoryForm
                    category={category}
                    isLoading={isLoading}
                    units={units}
                    dimensions={dimensions}
                    systems={systems}
                    onSaveCategoryNameUpdate={handleSaveCategoryNameUpdate}
                    onSaveCategoryDescriptionUpdate={handleSaveCategoryDescriptionUpdate}
                    onAddSimilarName={handleAddSimilarName}
                    onRemoveSimilarName={handleRemoveSimilarName}
                    onChangeServiceArea={handleChangeServiceArea}
                    onChangeItemLocation={handleChangeItemLocation}
                    onMoveParameter={handleMoveParameter}
                    onParameterAdd={() => {
                        setShowNewParamForm(true);
                        setNewParam(defaultNewParam());
                    }}
                    showNewParamForm={showNewParamForm}
                    onNewParamCancel={() => {
                        setShowNewParamForm(false);
                        setNewParam(defaultNewParam());
                    }}
                    onParameterRemove={async (index) => {
                        if (!category) return;

                        if (category.version == null) {
                            console.error("Category version missing — cannot apply patch");
                            return;
                        }

                        const param = category.parameters[index];
                        if (!param || !param.name?.trim()) return;

                        try {
                            await patchCategoryRemoveParameter(token, {
                                id: category.id,
                                version: category.version,
                                patchTask: "RemoveParameter",
                                parameterId: param.id,
                            });

                            const updated = await getCategoryById(token, category.id);
                            setCategory(updated);
                        } catch (err) {
                            logApiError(err);
                            throw err;
                        }
                    }}
                    onParameterChange={handleParameterChange as any}
                    onCategoryFieldChange={(field, value) =>
                        setCategory((prev) => prev ? { ...prev, [field]: value } : prev)
                    }
                    onCategorySave={handleCategorySave}
                    onSaveParameterNameUpdate={handleSaveParameterNameUpdate}
                    onSaveParameterDescriptionUpdate={handleSaveParameterDescriptionUpdate}
                    onSaveParameterUnitUpdate={handleSaveParameterUnitUpdate}
                    onSaveOptionEdit={handleSaveOptionEdit}
                    onRemoveOption={handleRemoveOption}
                    newParam={newParam as any}
                    onNewParamChange={(field, value) => {
                        setNewParam((prev) => ({ ...prev, [field]: value } as any));
                    }}
                    onNewParamSubmit={handleAddParameter}
                />
            )}
        </div>
    );
};

export default CategoryComponent;